// 业务侧渲染入口：包装 Viewer 闭环契约（@mindcloudxai/shared Viewer）。
// 控制思路：场景参数一次性输入（loadScene），运行期只做只读查询、
// 意图式命令与事件订阅；业务代码不直接 import 渲染实现包。

import {
  createAdapterViewer,
  type AvatarAnimationMapping,
  type CameraLike,
  type CameraOptions,
  type CollisionWorld,
  type EulerRotation,
  type GizmoMode,
  type JoystickMoveIntent,
  type SampleAnimTrackRouteOptions,
  type ScreenshotOptions,
  type Transform,
  type Vec3Like,
  type Viewer,
  type ViewerEventMap,
  type ViewerInputMode,
  type ViewerNavigationMode,
  type ViewerSceneOptions,
  type WalkMoveIntent,
  type WalkPhysicsConfig,
  type Track,
  type VrQualityPreset,
  AdapterViewer,
} from '@mindcloudxai_3d/core';
import { createViewer as createPlayCanvasViewer } from '@mindcloudxai_3d/playcanvas';
import { createLogger } from '@utils/logger';

const log = createLogger('renderer');

export interface ControllerInitOptions {
  container: HTMLElement;
  background?: string;
  camera?: CameraOptions;
  /** 调试用：切换底层渲染实现，默认 'playcanvas' */
  renderer?: 'three' | 'playcanvas';
  deviceType?: 'webgl' | 'webgpu';
  avatarUrl?: string;
  avatarEnabled?: boolean;
  avatarFollowCamera?: boolean;
  avatarMapping?: Partial<AvatarAnimationMapping>;
  /** 显示运行时性能浮层（MiniStats），调试用 */
  ministats?: boolean;
  /** 禁用 viewer 自身输入监听，适用于只读预览实例 */
  inputEnabled?: boolean;
}

const DEFAULT_CAMERA: CameraOptions = {
  position: { x: 0, y: 1.5, z: 4 },
  target: { x: 0, y: 0, z: 0 },
  fov: 60,
  near: 0.01,
  far: 1000,
};

export type SceneLoadOptions = Omit<ViewerSceneOptions, 'baseRotation'> & {
  baseRotation?: EulerRotation;
};

export class RendererController {
  private viewer: Viewer | null = null;
  private currentCamera: CameraOptions = DEFAULT_CAMERA;

  readonly camera = {
    get: () => {
      try {
        this.currentCamera = this.ensure().camera.get();
      } catch {
        return this.currentCamera;
      }
      return this.currentCamera;
    },
    set: (camera: CameraOptions) => {
      this.ensure().camera.set(camera);
      this.currentCamera = camera;
    },
    pick: (options: { clientX: number; clientY: number }) =>
      this.viewer?.camera.pick(options) ?? null,
    project: (point: Vec3Like) => this.viewer?.camera.project(point) ?? null,
    captureScreenshot: (options?: ScreenshotOptions) =>
      this.ensure().camera.captureScreenshot(options),
  };

  readonly navigation = {
    getMode: () => this.viewer?.navigation.getMode() ?? 'orbit',
    setMode: (mode: ViewerNavigationMode) => this.viewer?.navigation.setMode(mode),
    reset: () => this.viewer?.navigation.reset(),
    isWalkAvailable: () => this.viewer?.navigation.isWalkAvailable() ?? false,
    walk: {
      setMoveIntent: (intent: WalkMoveIntent) => this.viewer?.navigation.walk.setMoveIntent(intent),
      setJoystickIntent: (intent: JoystickMoveIntent) =>
        this.viewer?.navigation.walk.setJoystickIntent(intent),
      jump: () => this.viewer?.navigation.walk.jump(),
    },
    anim: {
      getState: () =>
        this.viewer?.navigation.anim.getState() ?? {
          hasAnimation: false,
          duration: 0,
          time: 0,
          paused: true,
          active: false,
        },
      play: () => this.viewer?.navigation.anim.play(),
      pause: () => this.viewer?.navigation.anim.pause(),
      togglePlayPause: () => this.viewer?.navigation.anim.togglePlayPause(),
      seek: (time: number) => this.viewer?.navigation.anim.seek(time),
      setSpeed: (speed: number) => this.viewer?.navigation.anim.setSpeed(speed),
      setTrack: (track: Track | null) => this.viewer?.navigation.anim.setTrack(track),
    },
  };

  readonly scene = {
    getAnnotations: () => this.viewer?.scene.getAnnotations() ?? [],
    focusAnnotation: (index: number) => this.viewer?.scene.focusAnnotation(index),
    showTrackRoute: (points: Vec3Like[] | null) => this.viewer?.scene.showTrackRoute?.(points),
    showCameraTrajectory: (poses: CameraLike[] | null) =>
      this.viewer?.scene.showCameraTrajectory?.(poses),
    showAnimTrackRoute: (track: Track | null, options?: SampleAnimTrackRouteOptions) =>
      this.viewer?.scene.showAnimTrackRoute?.(track, options),
    showPreviewCameraPose: (pose: CameraLike | null) =>
      this.viewer?.scene.showPreviewCameraPose?.(pose),
    setCameraGizmo: (pose: CameraLike | null, mode?: GizmoMode) =>
      this.viewer?.scene.setCameraGizmo?.(pose, mode),
  };

  readonly measure = {
    isActive: () => this.viewer?.measure.isActive() ?? false,
    setActive: (active: boolean) => this.viewer?.measure.setActive(active),
    toggle: () => this.viewer?.measure.toggle() ?? false,
    clear: () => this.viewer?.measure.clear(),
    setUnitSystem: (system: 'metric' | 'imperial') => this.viewer?.measure.setUnitSystem(system),
  };

  readonly avatar = {
    getState: () => this.viewer?.avatar.getState() ?? null,
    setEnabled: (enabled: boolean) => this.viewer?.avatar.setEnabled(enabled),
    setUrl: (url: string) => this.viewer?.avatar.setUrl(url),
    setFollowCamera: (enabled: boolean) => this.viewer?.avatar.setFollowCamera(enabled),
    setMapping: (mapping: Partial<AvatarAnimationMapping>) =>
      this.viewer?.avatar.setMapping(mapping),
  };

  readonly input = {
    setMode: (mode: ViewerInputMode) => this.viewer?.input.setMode(mode),
    setGamingControls: (enabled: boolean) => this.viewer?.input.setGamingControls(enabled),
  };

  readonly xr = {
    enterVr: (preset?: VrQualityPreset) => this.viewer?.xr.enterVr(preset),
  };

  readonly model = {
    addModel: (options: { url?: string; file?: File; name?: string }) =>
      this.viewer?.model.addModel(options) ?? Promise.reject(new Error('Viewer not initialized')),
    removeModel: (modelId: string) => this.viewer?.model.removeModel(modelId),
    duplicateModel: (modelId: string) =>
      this.viewer?.model.duplicateModel(modelId) ??
      Promise.reject(new Error('Viewer not initialized')),
    setModelTransform: (modelId: string, transform: Transform) =>
      this.viewer?.model.setModelTransform(modelId, transform),
    getModelTransform: (modelId: string) => this.viewer?.model.getModelTransform(modelId) ?? null,
    getModels: () => this.viewer?.model.getModels() ?? [],
    pickModel: (options: { clientX: number; clientY: number }) =>
      this.viewer?.model.pickModel(options) ?? Promise.resolve(null),
    setGizmo: (modelId: string, mode: GizmoMode | null) =>
      this.viewer?.model.setGizmo(modelId, mode),
    focusModel: (modelId: string) => this.viewer?.model.focusModel(modelId),
    setEditMode: (active: boolean) => this.viewer?.model.setEditMode(active),
  };

  async init(options: ControllerInitOptions): Promise<void> {
    this.dispose();
    log.info('init', { renderer: options.renderer ?? 'playcanvas' });
    const camera = options.camera ?? DEFAULT_CAMERA;
    const viewer = await resolveViewer(options.renderer ?? 'playcanvas');
    this.viewer = viewer;
    await viewer.init(options.container, {
      background: options.background ?? '#0f172a',
      camera,
      renderer: options.deviceType,
      avatarUrl: options.avatarUrl,
      avatarEnabled: options.avatarEnabled,
      avatarFollowCamera: options.avatarFollowCamera,
      avatarMapping: options.avatarMapping,
      ministats: options.ministats,
      inputEnabled: options.inputEnabled,
    });
    this.currentCamera = camera;
  }

  /** 一次性输入场景参数（splat/collision/运镜/热点），viewer 闭环执行。 */
  async loadScene(options: SceneLoadOptions): Promise<void> {
    log.info('loadScene', { url: options.url });
    await this.ensure().loadScene(options);
  }

  // ---------------------------------------------------------------------------
  // 事件
  // ---------------------------------------------------------------------------

  on<K extends keyof ViewerEventMap>(event: K, cb: ViewerEventMap[K]): void {
    this.viewer?.on(event, cb);
  }

  off<K extends keyof ViewerEventMap>(event: K, cb: ViewerEventMap[K]): void {
    this.viewer?.off(event, cb);
  }

  // ---------------------------------------------------------------------------
  // 生命周期
  // ---------------------------------------------------------------------------

  resize(width: number, height: number): void {
    // init 是异步的，ResizeObserver 可能在 viewer 就绪前触发，静默跳过即可
    this.viewer?.resize(width, height);
  }

  flushFrame(deltaTime?: number): void {
    this.viewer?.flushFrame(deltaTime);
  }

  dispose(): void {
    this.viewer?.dispose();
    this.viewer = null;
  }

  // ---------------------------------------------------------------------------
  // 后端特有扩展（three walk/collision；playcanvas 由 viewer 内部闭环，no-op）
  // ---------------------------------------------------------------------------

  setCollisionWorld(world: CollisionWorld): void {
    this.adapterOf()?.setCollisionWorld?.(world);
  }

  setWalkPhysics(config: WalkPhysicsConfig): void {
    this.adapterOf()?.setWalkPhysics?.(config);
  }

  // ---------------------------------------------------------------------------
  // 内部
  // ---------------------------------------------------------------------------

  private adapterOf() {
    return this.viewer instanceof AdapterViewer ? this.viewer.adapter : null;
  }

  private ensure(): Viewer {
    if (!this.viewer) throw new Error('RendererController is not initialized');
    return this.viewer;
  }
}

/** 按 renderer 参数解析 Viewer 实现；默认 PlayCanvas，Three 保持动态 import。 */
async function resolveViewer(renderer: 'three' | 'playcanvas'): Promise<Viewer> {
  if (renderer === 'playcanvas') {
    return createPlayCanvasViewer();
  }
  // three 暂无原生 viewer，经 AdapterViewer shim 提供 Viewer 契约（无运镜闭环）
  const { createThreeAdapter } = await import('@mindcloudxai_3d/three');
  return createAdapterViewer(createThreeAdapter());
}
